import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Color from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { common, createLowlight } from 'lowlight'
import Mention from '@tiptap/extension-mention'
import createMentionSuggestion from './mentionSuggestion'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, ListChecks,
  Code, Code2, Quote, Minus,
  Link as LinkIcon, Unlink, Image as ImageIcon,
  Table as TableIcon, Smile,
  MoreHorizontal, ChevronDown, Plus, Trash2,
  Subscript as SubscriptIcon, Superscript as SuperscriptIcon,
  AtSign,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const lowlight = createLowlight(common)

// Jira-style text colors
const TEXT_COLORS = [
  { name: 'Default', color: null },
  { name: 'Dark gray', color: '#172b4d' },
  { name: 'Gray', color: '#6b778c' },
  { name: 'Light gray', color: '#97a0af' },
  { name: 'Purple', color: '#6554c0' },
  { name: 'Teal', color: '#00b8d9' },
  { name: 'Green', color: '#36b37e' },
  { name: 'Red', color: '#ff5630' },
  { name: 'Orange', color: '#ff991f' },
  { name: 'Blue', color: '#0065ff' },
]

// Common emoji set
const EMOJI_LIST = [
  '😀', '😂', '😊', '🙂', '😍', '🤔', '😎', '👍', '👎', '👏',
  '🎉', '🔥', '❤️', '💯', '✅', '❌', '⭐', '🚀', '💡', '⚡',
  '🐛', '🔧', '📝', '📌', '🏷️', '🔗', '📎', '📊', '📈', '🎯',
  '⏰', '🔒', '🔓', '💬', '👀', '🤝', '🙌', '💪', '🎨', '🧪',
]

// ── Toolbar button ──────────────────────────────────────────────
const ToolbarBtn = React.forwardRef(({ onClick, isActive, disabled, title, children, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    title={title}
    disabled={disabled}
    className={cn(
      'inline-flex items-center justify-center rounded h-8 w-8 text-muted-foreground transition-colors',
      'hover:bg-accent hover:text-accent-foreground',
      'disabled:opacity-40 disabled:pointer-events-none',
      isActive && 'bg-accent text-accent-foreground',
      className,
    )}
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
))

const Divider = () => <div className="w-px h-6 bg-border mx-1 shrink-0" />

// ── Text style dropdown (Tt ↓) ─────────────────────────────────
const TextStyleDropdown = ({ editor }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded h-8 px-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <span className="text-xs font-semibold tracking-tight">Tt</span>
        <ChevronDown className="h-3 w-3" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-52">
      <DropdownMenuItem onClick={() => editor.chain().focus().setParagraph().run()}>
        <span className="text-sm">Normal text</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
        <span className="text-xl font-bold">Heading 1</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <span className="text-lg font-bold">Heading 2</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <span className="text-base font-semibold">Heading 3</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}>
        <span className="text-sm font-semibold">Heading 4</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}>
        <span className="text-xs font-semibold">Heading 5</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}>
        <span className="text-xs font-medium text-muted-foreground">Heading 6</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

// ── More formatting (··· dropdown) ──────────────────────────────
const MoreFormattingDropdown = ({ editor }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        title="More formatting"
        className="inline-flex items-center justify-center rounded h-8 w-8 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-48">
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <UnderlineIcon className="h-4 w-4 mr-2" />
        <span>Underline</span>
        <span className="ml-auto text-xs text-muted-foreground">⌘U</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4 mr-2" />
        <span>Strikethrough</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleCode().run()}>
        <Code className="h-4 w-4 mr-2" />
        <span>Inline code</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleSubscript().run()}>
        <SubscriptIcon className="h-4 w-4 mr-2" />
        <span>Subscript</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleSuperscript().run()}>
        <SuperscriptIcon className="h-4 w-4 mr-2" />
        <span>Superscript</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

// ── Text color (A ↓) ────────────────────────────────────────────
const TextColorDropdown = ({ editor }) => {
  const currentColor = editor.getAttributes('textStyle').color

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Text color"
          className="inline-flex items-center gap-0.5 rounded h-8 px-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <span className="relative">
            <span className="text-sm font-bold" style={{ color: currentColor || undefined }}>A</span>
            <span
              className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded-full"
              style={{ backgroundColor: currentColor || '#172b4d' }}
            />
          </span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <p className="text-xs font-medium text-muted-foreground mb-2">Text color</p>
        <div className="flex gap-1 flex-wrap max-w-[200px]">
          {TEXT_COLORS.map(({ name, color }) => (
            <button
              key={name}
              type="button"
              title={name}
              className={cn(
                'w-6 h-6 rounded border border-border hover:scale-110 transition-transform flex items-center justify-center',
                !color && 'bg-background',
              )}
              style={{ backgroundColor: color || undefined }}
              onClick={() => {
                if (color) {
                  editor.chain().focus().setColor(color).run()
                } else {
                  editor.chain().focus().unsetColor().run()
                }
              }}
            >
              {!color && <span className="text-xs">⌀</span>}
            </button>
          ))}
        </div>
        <DropdownMenuSeparator className="my-2" />
        <p className="text-xs font-medium text-muted-foreground mb-2">Highlight</p>
        <div className="flex gap-1 flex-wrap max-w-[200px]">
          {['#fffee1', '#ddfce1', '#dbf0fd', '#ede3fc', '#fde8e8', '#fef0dc', null].map((color, i) => (
            <button
              key={i}
              type="button"
              title={color ? `Highlight ${color}` : 'No highlight'}
              className={cn(
                'w-6 h-6 rounded border border-border hover:scale-110 transition-transform flex items-center justify-center',
              )}
              style={{ backgroundColor: color || 'transparent' }}
              onClick={() => {
                if (color) {
                  editor.chain().focus().toggleHighlight({ color }).run()
                } else {
                  editor.chain().focus().unsetHighlight().run()
                }
              }}
            >
              {!color && <span className="text-xs">⌀</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ── Link popover ────────────────────────────────────────────────
const LinkPopover = ({ editor }) => {
  const [url, setUrl] = useState('')
  const [open, setOpen] = useState(false)

  const setLink = useCallback(() => {
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run()
    }
    setUrl('')
    setOpen(false)
  }, [editor, url])

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setUrl(editor.getAttributes('link').href || '') }}>
      <PopoverTrigger asChild>
        <ToolbarBtn title="Link" isActive={editor.isActive('link')}>
          <LinkIcon className="h-4 w-4" />
        </ToolbarBtn>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <p className="text-xs font-medium text-muted-foreground mb-2">Paste link</p>
        <div className="flex gap-2">
          <input
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setLink()}
            className="flex-1 border rounded-md px-2.5 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            autoFocus
          />
          <Button size="sm" onClick={setLink}>Apply</Button>
          {editor.isActive('link') && (
            <Button
              size="sm" variant="ghost"
              onClick={() => { editor.chain().focus().unsetLink().run(); setOpen(false) }}
            >
              <Unlink className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ── Emoji picker ────────────────────────────────────────────────
const EmojiPicker = ({ editor }) => (
  <Popover>
    <PopoverTrigger asChild>
      <ToolbarBtn title="Emoji">
        <Smile className="h-4 w-4" />
      </ToolbarBtn>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-2" align="start">
      <div className="grid grid-cols-10 gap-0.5">
        {EMOJI_LIST.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-accent transition-colors text-base"
            onClick={() => editor.chain().focus().insertContent(emoji).run()}
          >
            {emoji}
          </button>
        ))}
      </div>
    </PopoverContent>
  </Popover>
)

// ── Table menu ──────────────────────────────────────────────────
const TableMenu = ({ editor }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <ToolbarBtn title="Table">
        <TableIcon className="h-4 w-4" />
      </ToolbarBtn>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-48">
      <DropdownMenuItem onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
        <Plus className="h-4 w-4 mr-2" /> Insert table
      </DropdownMenuItem>
      {editor.can().deleteTable() && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => editor.chain().focus().addColumnAfter().run()}>
            Add column after
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().addColumnBefore().run()}>
            Add column before
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().addRowAfter().run()}>
            Add row after
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().addRowBefore().run()}>
            Add row before
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => editor.chain().focus().deleteColumn().run()}>
            Delete column
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().deleteRow().run()}>
            Delete row
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => editor.chain().focus().mergeCells().run()}>
            Merge cells
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => editor.chain().focus().splitCell().run()}>
            Split cell
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => editor.chain().focus().deleteTable().run()} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-2" /> Delete table
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
)

// ── More insert options (+ ↓) ───────────────────────────────────
const MoreInsertDropdown = ({ editor }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        title="More"
        className="inline-flex items-center gap-0.5 rounded h-8 px-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <Plus className="h-4 w-4" />
        <ChevronDown className="h-3 w-3" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-48">
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="h-4 w-4 mr-2" />
        <span>Quote</span>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="h-4 w-4 mr-2" />
        <span>Divider</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => editor.chain().focus().toggleTaskList().run()}>
        <ListChecks className="h-4 w-4 mr-2" />
        <span>Action item</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

// ── Image toolbar button (file picker) ──────────────────────────
const ImageButton = ({ onImageUpload }) => {
  const fileInputRef = useRef(null)
  return (
    <>
      <ToolbarBtn title="Add image" onClick={() => fileInputRef.current?.click()}>
        <ImageIcon className="h-4 w-4" />
      </ToolbarBtn>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || [])
          files.forEach(f => onImageUpload?.(f))
          e.target.value = ''
        }}
      />
    </>
  )
}

// ── Main toolbar (Jira layout) ──────────────────────────────────
const MenuBar = ({ editor, onImageUpload }) => {
  if (!editor) return null

  return (
    <div className="flex items-center gap-0.5 border-b px-1.5 py-1 flex-wrap bg-muted/30">
      <TextStyleDropdown editor={editor} />
      <Divider />
      <ToolbarBtn title="Bold ⌘B" isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </ToolbarBtn>
      <ToolbarBtn title="Italic ⌘I" isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </ToolbarBtn>
      <MoreFormattingDropdown editor={editor} />
      <Divider />
      <TextColorDropdown editor={editor} />
      <Divider />
      <ToolbarBtn title="Bullet list" isActive={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </ToolbarBtn>
      <ToolbarBtn title="Numbered list" isActive={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </ToolbarBtn>
      <Divider />
      <ToolbarBtn title="Action item" isActive={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}>
        <ListChecks className="h-4 w-4" />
      </ToolbarBtn>
      <LinkPopover editor={editor} />
      {onImageUpload && <ImageButton onImageUpload={onImageUpload} />}
      <ToolbarBtn title="Mention someone (@)" onClick={() => {
        editor.chain().focus().insertContent('@').run()
      }}>
        <AtSign className="h-4 w-4" />
      </ToolbarBtn>
      <EmojiPicker editor={editor} />
      <TableMenu editor={editor} />
      <ToolbarBtn title="Code snippet" isActive={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
        <Code2 className="h-4 w-4" />
      </ToolbarBtn>
      <MoreInsertDropdown editor={editor} />
    </div>
  )
}

// ── Helper: read file as base64 ─────────────────────────────────
const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

// ── Main component ──────────────────────────────────────────────
const RichTextEditor = ({
  content = '',
  onChange,
  placeholder = 'Type something...',
  minHeight = 150,
  readOnly = false,
  showToolbar = true,
  onImageUpload,
  mentionUsers = [],
}) => {
  const onImageUploadRef = useRef(onImageUpload)
  onImageUploadRef.current = onImageUpload

  const mentionSuggestion = useMemo(
    () => createMentionSuggestion(mentionUsers),
    [mentionUsers]
  )

  // Handle a single image file: show base64 preview, upload, swap to real URL
  const handleImageFile = useCallback(async (file, editorInstance) => {
    if (!file?.type.startsWith('image/') || !editorInstance) return
    const uploadFn = onImageUploadRef.current

    if (uploadFn) {
      // Show base64 preview immediately
      const base64 = await readFileAsDataURL(file)
      editorInstance.chain().focus().setImage({ src: base64 }).run()

      // Upload in background, then swap base64 → real URL
      try {
        const url = await uploadFn(file)
        if (url) {
          const { state } = editorInstance
          const tr = state.tr
          let found = false
          state.doc.descendants((node, pos) => {
            if (!found && node.type.name === 'image' && node.attrs.src === base64) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: url })
              found = true
            }
          })
          if (found) editorInstance.view.dispatch(tr)
        }
      } catch (err) {
        console.error('Image upload failed, keeping preview:', err)
      }
    } else {
      // No upload handler (e.g. create dialog) — use base64
      const base64 = await readFileAsDataURL(file)
      editorInstance.chain().focus().setImage({ src: base64 }).run()
    }
  }, [])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Placeholder.configure({ placeholder }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline cursor-pointer' },
      }),
      Image.configure({
        allowBase64: true,
        inline: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({ multicolor: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Subscript,
      Superscript,
      TextStyle,
      Color,
      CodeBlockLowlight.configure({ lowlight }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: mentionSuggestion,
      }),
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML())
    },
    editorProps: {
      handlePaste(view, event) {
        const items = event.clipboardData?.items
        if (!items) return false
        const imageFiles = []
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile()
            if (file) imageFiles.push(file)
          }
        }
        if (imageFiles.length === 0) return false
        event.preventDefault()
        // Use FileReader in sync callback, then insert async
        for (const file of imageFiles) {
          const reader = new FileReader()
          reader.onload = () => {
            if (!editor) return
            const base64 = reader.result
            editor.chain().focus().setImage({ src: base64 }).run()
            // Upload in background
            const uploadFn = onImageUploadRef.current
            if (uploadFn) {
              uploadFn(file).then((url) => {
                if (!url || !editor) return
                const { state } = editor
                const tr = state.tr
                let found = false
                state.doc.descendants((node, pos) => {
                  if (!found && node.type.name === 'image' && node.attrs.src === base64) {
                    tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: url })
                    found = true
                  }
                })
                if (found) editor.view.dispatch(tr)
              }).catch(err => console.error('Image upload failed:', err))
            }
          }
          reader.readAsDataURL(file)
        }
        return true
      },
      handleDrop(view, event) {
        const files = event.dataTransfer?.files
        if (!files?.length) return false
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'))
        if (imageFiles.length === 0) return false
        event.preventDefault()
        for (const file of imageFiles) {
          const reader = new FileReader()
          reader.onload = () => {
            if (!editor) return
            const base64 = reader.result
            editor.chain().focus().setImage({ src: base64 }).run()
            const uploadFn = onImageUploadRef.current
            if (uploadFn) {
              uploadFn(file).then((url) => {
                if (!url || !editor) return
                const { state } = editor
                const tr = state.tr
                let found = false
                state.doc.descendants((node, pos) => {
                  if (!found && node.type.name === 'image' && node.attrs.src === base64) {
                    tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: url })
                    found = true
                  }
                })
                if (found) editor.view.dispatch(tr)
              }).catch(err => console.error('Image upload failed:', err))
            }
          }
          reader.readAsDataURL(file)
        }
        return true
      },
    },
  })

  return (
    <div className={cn(
      'border rounded-lg overflow-hidden bg-background',
      readOnly && 'border-transparent',
      !readOnly && 'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1',
    )}>
      {showToolbar && !readOnly && <MenuBar editor={editor} onImageUpload={onImageUpload ? (file) => handleImageFile(file, editor) : undefined} />}
      <EditorContent
        editor={editor}
        className={cn(
          'prose prose-sm max-w-none px-3 py-2 focus-within:outline-none text-sm',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[inherit]',
          // Placeholder
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground/50',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0',
          // Task list
          '[&_.ProseMirror_ul[data-type=taskList]]:list-none [&_.ProseMirror_ul[data-type=taskList]]:pl-0',
          '[&_.ProseMirror_ul[data-type=taskList]_li]:flex [&_.ProseMirror_ul[data-type=taskList]_li]:items-start [&_.ProseMirror_ul[data-type=taskList]_li]:gap-2',
          '[&_.ProseMirror_ul[data-type=taskList]_li_label]:mt-0.5',
          '[&_.ProseMirror_ul[data-type=taskList]_li_div]:flex-1',
          // Table
          '[&_.ProseMirror_table]:border-collapse [&_.ProseMirror_table]:w-full [&_.ProseMirror_table]:table-fixed [&_.ProseMirror_table]:my-2',
          '[&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-border [&_.ProseMirror_td]:p-2 [&_.ProseMirror_td]:relative [&_.ProseMirror_td]:align-top',
          '[&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-border [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:bg-muted [&_.ProseMirror_th]:font-semibold [&_.ProseMirror_th]:align-top',
          '[&_.ProseMirror_.selectedCell]:bg-primary/10',
          // Code block
          '[&_.ProseMirror_pre]:bg-muted [&_.ProseMirror_pre]:rounded-md [&_.ProseMirror_pre]:p-3 [&_.ProseMirror_pre]:font-mono [&_.ProseMirror_pre]:text-xs [&_.ProseMirror_pre]:my-2',
          '[&_.ProseMirror_pre_code]:bg-transparent [&_.ProseMirror_pre_code]:p-0',
          // Inline code
          '[&_.ProseMirror_code]:bg-muted [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-xs [&_.ProseMirror_code]:text-destructive',
          // Blockquote
          '[&_.ProseMirror_blockquote]:border-l-[3px] [&_.ProseMirror_blockquote]:border-border [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:text-muted-foreground [&_.ProseMirror_blockquote]:my-2',
          // Image
          '[&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:rounded [&_.ProseMirror_img]:my-2 [&_.ProseMirror_img]:cursor-default',
          // Horizontal rule
          '[&_.ProseMirror_hr]:border-border [&_.ProseMirror_hr]:my-4',
          // Highlight
          '[&_.ProseMirror_mark]:rounded-sm [&_.ProseMirror_mark]:px-0.5',
        )}
        style={{ minHeight }}
      />
    </div>
  )
}

export default RichTextEditor
